import { z } from 'zod';

/**
 * Mobile emulation tier (Migration 065 / Delivery roadmap Tier 2.7).
 *
 * The editor palette exposes gesture step types (swipe, pinch, long-press)
 * for authoring under mobile emulation, but the runner does **not** execute
 * real touch gestures (P0-5: those steps fail honestly rather than silently
 * mapping to click/scroll). The capability matrix can still target iOS /
 * Android device profiles. This module adds the missing two layers:
 *
 *   - **Device profile registry** — a curated list of Playwright
 *     `devices[...]` presets we surface in the launcher's "Device"
 *     dropdown. Each profile carries the user-agent + viewport +
 *     deviceScaleFactor + geolocation defaults the runner needs.
 *
 *   - **Network preset registry** — wifi / 4g / 3g-slow / offline.
 *     Each preset maps to Playwright's `route.fulfill` /
 *     CDP `Network.emulateNetworkConditions` knobs. Operators can
 *     override the throttle numbers via admin config; the named
 *     preset is what the launcher shows.
 *
 * Both registries are versioned static data — they ship in the
 * binary, are validated against the Zod schema below at boot, and
 * are surfaced via `GET /api/runs/devices` so the launcher doesn't
 * round-trip the full catalogue on every flow open.
 */

export const NETWORK_PRESET_IDS = ['wifi', '4g', '3g-slow', 'offline'] as const;
export type NetworkPresetId = (typeof NETWORK_PRESET_IDS)[number];
export const NetworkPresetIdSchema = z.enum(NETWORK_PRESET_IDS);

export const NetworkPresetSchema = z.object({
  id: NetworkPresetIdSchema,
  label: z.string().min(1),
  /**
   * `null` means "no throttle, no offline". The runner skips
   * `Network.emulateNetworkConditions` entirely. Used by `wifi`.
   */
  download: z.number().int().nonnegative().nullable(),
  upload: z.number().int().nonnegative().nullable(),
  latency: z.number().int().nonnegative().nullable(),
  offline: z.boolean(),
});
export type NetworkPreset = z.infer<typeof NetworkPresetSchema>;

/**
 * Curated network presets. Numbers borrowed from Chrome DevTools
 * "Slow 3G" / "Fast 3G" / "Regular 4G" — accurate enough for QA
 * gating without claiming to be carrier-realistic.
 */
export const NETWORK_PRESETS: Record<NetworkPresetId, NetworkPreset> = {
  wifi: {
    id: 'wifi',
    label: 'Wi-Fi (no throttle)',
    download: null,
    upload: null,
    latency: null,
    offline: false,
  },
  '4g': {
    id: '4g',
    label: '4G (Regular)',
    download: 4_000_000 / 8,
    upload: 3_000_000 / 8,
    latency: 70,
    offline: false,
  },
  '3g-slow': {
    id: '3g-slow',
    label: '3G (Slow)',
    download: 500_000 / 8,
    upload: 500_000 / 8,
    latency: 400,
    offline: false,
  },
  offline: {
    id: 'offline',
    label: 'Offline',
    download: 0,
    upload: 0,
    latency: 0,
    offline: true,
  },
};

export const DeviceProfileSchema = z.object({
  /**
   * The Playwright `devices[...]` key — the runner uses this verbatim
   * when constructing the BrowserContext. Stable across Playwright
   * upgrades because we pin a tested set; new devices land via PR.
   */
  id: z.string().min(1).max(64),
  label: z.string().min(1).max(64),
  category: z.enum(['phone', 'tablet']),
  /** Mirrored from the Playwright preset for the picker UI. */
  viewport: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  /** Display-friendly userAgent prefix, not the runner's authoritative copy. */
  userAgent: z.string().min(1),
  hasTouch: z.boolean(),
  isMobile: z.boolean(),
  os: z.enum(['ios', 'android']),
  /** Reserved for visual-check key derivation. */
  deviceScaleFactor: z.number().positive(),
});
export type DeviceProfile = z.infer<typeof DeviceProfileSchema>;

/**
 * Curated device catalogue. Keep this list short on purpose — the
 * top 12 devices that cover ~90% of mobile web traffic per
 * statcounter.com (2026 H1). Extending the list is a contract
 * change so we don't ship 60-row dropdowns into the launcher.
 */
export const DEVICE_PROFILES: DeviceProfile[] = [
  {
    id: 'iPhone 15',
    label: 'iPhone 15',
    category: 'phone',
    viewport: { width: 393, height: 852 },
    userAgent: 'iPhone OS 17 / Safari',
    hasTouch: true,
    isMobile: true,
    os: 'ios',
    deviceScaleFactor: 3,
  },
  {
    id: 'iPhone 14',
    label: 'iPhone 14',
    category: 'phone',
    viewport: { width: 390, height: 844 },
    userAgent: 'iPhone OS 16 / Safari',
    hasTouch: true,
    isMobile: true,
    os: 'ios',
    deviceScaleFactor: 3,
  },
  {
    id: 'iPhone 13',
    label: 'iPhone 13',
    category: 'phone',
    viewport: { width: 390, height: 844 },
    userAgent: 'iPhone OS 15 / Safari',
    hasTouch: true,
    isMobile: true,
    os: 'ios',
    deviceScaleFactor: 3,
  },
  {
    id: 'iPhone SE',
    label: 'iPhone SE',
    category: 'phone',
    viewport: { width: 375, height: 667 },
    userAgent: 'iPhone OS 16 / Safari',
    hasTouch: true,
    isMobile: true,
    os: 'ios',
    deviceScaleFactor: 2,
  },
  {
    id: 'Pixel 8',
    label: 'Pixel 8',
    category: 'phone',
    viewport: { width: 412, height: 915 },
    userAgent: 'Android 14 / Chrome',
    hasTouch: true,
    isMobile: true,
    os: 'android',
    deviceScaleFactor: 2.625,
  },
  {
    id: 'Pixel 7',
    label: 'Pixel 7',
    category: 'phone',
    viewport: { width: 412, height: 915 },
    userAgent: 'Android 13 / Chrome',
    hasTouch: true,
    isMobile: true,
    os: 'android',
    deviceScaleFactor: 2.625,
  },
  {
    id: 'Galaxy S23',
    label: 'Galaxy S23',
    category: 'phone',
    viewport: { width: 360, height: 780 },
    userAgent: 'Android 13 / Chrome',
    hasTouch: true,
    isMobile: true,
    os: 'android',
    deviceScaleFactor: 3,
  },
  {
    id: 'Galaxy S22',
    label: 'Galaxy S22',
    category: 'phone',
    viewport: { width: 360, height: 780 },
    userAgent: 'Android 12 / Chrome',
    hasTouch: true,
    isMobile: true,
    os: 'android',
    deviceScaleFactor: 3,
  },
  {
    id: 'iPad Pro 12.9',
    label: 'iPad Pro 12.9"',
    category: 'tablet',
    viewport: { width: 1024, height: 1366 },
    userAgent: 'iPad OS 17 / Safari',
    hasTouch: true,
    isMobile: false,
    os: 'ios',
    deviceScaleFactor: 2,
  },
  {
    id: 'iPad Air',
    label: 'iPad Air',
    category: 'tablet',
    viewport: { width: 820, height: 1180 },
    userAgent: 'iPad OS 17 / Safari',
    hasTouch: true,
    isMobile: false,
    os: 'ios',
    deviceScaleFactor: 2,
  },
  {
    id: 'Galaxy Tab S9',
    label: 'Galaxy Tab S9',
    category: 'tablet',
    viewport: { width: 800, height: 1340 },
    userAgent: 'Android 13 / Chrome',
    hasTouch: true,
    isMobile: false,
    os: 'android',
    deviceScaleFactor: 2,
  },
  {
    id: 'Pixel Tablet',
    label: 'Pixel Tablet',
    category: 'tablet',
    viewport: { width: 800, height: 1280 },
    userAgent: 'Android 14 / Chrome',
    hasTouch: true,
    isMobile: false,
    os: 'android',
    deviceScaleFactor: 2,
  },
];

const DEVICE_INDEX: Map<string, DeviceProfile> = new Map(DEVICE_PROFILES.map((p) => [p.id, p]));

export function findDeviceProfile(id: string | null | undefined): DeviceProfile | null {
  if (!id) return null;
  return DEVICE_INDEX.get(id) ?? null;
}

export function findNetworkPreset(id: string | null | undefined): NetworkPreset | null {
  if (!id) return null;
  if ((NETWORK_PRESET_IDS as readonly string[]).includes(id)) {
    return NETWORK_PRESETS[id as NetworkPresetId];
  }
  return null;
}

/**
 * `defer-mobile-geolocation-presets` (2026-05-20) — extends the
 * minimal lat/lon row that `defer-mobile-geolocation` shipped on
 * 2026-05-19 with the four optional fields the W3C Geolocation
 * API actually surfaces (`altitude`, `altitudeAccuracy`,
 * `heading`, `speed`) plus an optional waypoint sequence so a
 * test can simulate a device moving along a path
 * (geofence tests, "device on a train" scenarios).
 *
 * Order semantics: when both `position` (single pin) and
 * `waypoints` (timed sequence) are present, the picker treats
 * the single pin as the initial position and the runner
 * interpolates between the waypoints over the run duration.
 */
export const GeolocationWaypointSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  /**
   * Milliseconds the runner should "stay" at this waypoint
   * before moving on to the next. 0 means "snap to next ASAP".
   */
  dwellMs: z.number().int().nonnegative().max(3_600_000).optional(),
});
export type GeolocationWaypoint = z.infer<typeof GeolocationWaypointSchema>;

export const GeolocationSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracy: z.number().nonnegative().optional(),
  /** Optional metre altitude. */
  altitude: z.number().optional(),
  altitudeAccuracy: z.number().nonnegative().optional(),
  /** Direction in degrees (0 == north, 90 == east). */
  heading: z.number().min(0).max(360).optional(),
  /** Speed in metres per second. */
  speed: z.number().nonnegative().optional(),
  /** Optional waypoint sequence the runner interpolates between. */
  waypoints: z.array(GeolocationWaypointSchema).max(50).optional(),
});
export type Geolocation = z.infer<typeof GeolocationSchema>;

export const MobileEmulationSchema = z.object({
  device: z.string().min(1).max(64),
  network: NetworkPresetIdSchema.default('wifi'),
  /** Optional GPS coords. Geolocation grants are scoped to the run. */
  geolocation: GeolocationSchema.optional(),
});
export type MobileEmulation = z.infer<typeof MobileEmulationSchema>;

export const ListDevicesResponseSchema = z.object({
  devices: z.array(DeviceProfileSchema),
  networkPresets: z.array(NetworkPresetSchema),
});
export type ListDevicesResponse = z.infer<typeof ListDevicesResponseSchema>;

/**
 * Validate the registry at module-load time. If a profile we
 * shipped doesn't match the schema we want to know at boot, not
 * three weeks into a runner regression.
 */
{
  for (const p of DEVICE_PROFILES) {
    const r = DeviceProfileSchema.safeParse(p);
    if (!r.success) {
      throw new Error(`Invalid bundled device profile ${p.id}: ${r.error.message}`);
    }
  }
}
